// File: apps/web/src/analysis/premium/sales-tables.ts

import { formatNumber, formatCurrency } from '@/utils/string'
import * as $store from '@/store'
// --- START JOB 14 FIXES (IMPORTS) ---
import { AlsoStoreFn, createMaybeAlsoStoreFn, maybeAlsoStore } from '@/analysis/utils/store-helpers'
import { formatNumber as formatNumberUtil, formatCurrency as formatCurrencyUtil, formatDecimalBasedPercentage } from '@/utils/string'
import { deepmerge } from 'deepmerge-ts'
// --- END JOB 14 FIXES (IMPORTS) ---


export function generateTop10MenuTable(summaries: any[], containerId: string, sortBy: 'quantity' | 'revenue') {
  const container = document.getElementById(containerId)
  if (!container) return

  const menuData = new Map<string, { quantity: number, revenue: number, category: string }>()

  const hiddenMenus = $store.getConfigValue('hiddenMenus') || []
  const hiddenCategories = $store.getConfigValue('hiddenCategories') || []

  // Aggregate data from all summaries
  summaries.forEach((summary) => {
    if (!summary.menuItemQuantities) return
    for (const category in summary.menuItemQuantities) {
      // FIX: Check for hidden categories
      if (hiddenCategories.includes(category)) continue

      for (const itemName in summary.menuItemQuantities[category]) {
        // FIX: Check for hidden menu items
        if (hiddenMenus.includes(itemName)) continue

        const quantity = summary.menuItemQuantities[category][itemName]
        const revenue = summary.menuItemRevenues?.[category]?.[itemName] || 0

        if (menuData.has(itemName)) {
          const existing = menuData.get(itemName)!
          existing.quantity += quantity
          existing.revenue += revenue
        } else {
           menuData.set(itemName, { quantity, revenue, category })
        }
      }
    }
  })

  // Convert map to array, sort, and take top 10
  const sortedData = Array.from(menuData.entries())
    .sort(([, a], [, b]) => b[sortBy] - a[sortBy])
    .slice(0, 10)

  if (sortedData.length === 0) {
    container.innerHTML = '<p class="text-gray-400 text-center py-8">No menu data available for this period.</p>'
    return
  }

  // Generate HTML table
  let tableHtml = `
        <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
                <thead class="text-left text-gray-500">
                    <tr>
                        <th class="p-2 font-medium">#</th>
                        <th class="p-2 font-medium">Menu Name</th>
                        <th class="p-2 font-medium">Category</th>
                        <th class="p-2 font-medium text-right">${sortBy === 'quantity' ? 'Quantity' : 'Revenue'}</th>
                    </tr>
                </thead>
                <tbody>`

  sortedData.forEach(([name, data], index) => {
    const value = sortBy === 'quantity' ? formatNumber(data.quantity) : formatCurrency(data.revenue)
    tableHtml += `
            <tr class="border-t">
                <td class="p-2 text-center font-medium text-gray-600">${index + 1}</td>
                <td class="p-2 font-medium text-gray-800">${name}</td>
                <td class="p-2 text-gray-600">${data.category}</td>
                <td class="p-2 text-right font-mono">${value}</td>
            </tr>`
  })

  tableHtml += `</tbody></table></div>`
  container.innerHTML = tableHtml
}

// --- START MODIFICATION (JOB_14) ---

/**
 * Generates a table comparing the Top 10 menu items between two periods (Period B's top 10).
 */
export function generateTop10ComparisonTable(
  periodAData: any[], 
  periodBData: any[], 
  containerId: string, 
  sortBy: 'quantity' | 'revenue',
  config?: { alsoStore?: AlsoStoreFn } // <-- ADDED CONFIG
) {
  const container = document.getElementById(containerId)
  if (!container) return

  const hasRevenueData = (periodAData.length > 0 && periodAData[0]?.menuItemRevenues) || (periodBData.length > 0 && periodBData[0]?.menuItemRevenues)

  if (sortBy === 'revenue' && !hasRevenueData) {
    container.innerHTML = '<p class="text-gray-400 text-center py-8">Revenue data per menu item is not available in the summaries.</p>'
    return
  }

  // --- START MODIFICATION (JOB 17 FIX) ---
  const aggregateData = (summaries: any[]): Map<string, { quantity: number, revenue: number, category: string }> => {
    // Fetch exclusion lists from the store
    const hiddenMenus = $store.getConfigValue('hiddenMenus') || []
    const hiddenCategories = $store.getConfigValue('hiddenCategories') || []
    
    const menuData = new Map()
    summaries.forEach((summary) => {
      if (!summary.menuItemQuantities) return

      for (const category in summary.menuItemQuantities) {
        
        // 1. CHECK FOR HIDDEN CATEGORIES
        if (hiddenCategories.includes(category)) continue 

        for (const itemName in summary.menuItemQuantities[category]) {
          
          // 2. CHECK FOR HIDDEN MENUS
          if (hiddenMenus.includes(itemName)) continue

          const quantity = summary.menuItemQuantities[category][itemName]
          const revenue = summary.menuItemRevenues?.[category]?.[itemName] || 0

          if (menuData.has(itemName)) {
            const existing = menuData.get(itemName)!
            existing.quantity += quantity
            existing.revenue += revenue
          } else {
            menuData.set(itemName, { quantity, revenue, category })
          }
        }
      }
    })
    return menuData
  }
  // --- END MODIFICATION (JOB 17 FIX) ---

  const dataA = aggregateData(periodAData)
  const dataB = aggregateData(periodBData)

  const combinedKeys = new Set([...dataA.keys(), ...dataB.keys()])

  const combinedData = Array.from(combinedKeys).map((name) => {
    const itemA = dataA.get(name) || { quantity: 0, revenue: 0, category: '' }
    const itemB = dataB.get(name) || { quantity: 0, revenue: 0, category: '' }
    return {
      name,
      category: itemB.category || itemA.category,
      quantityA: itemA.quantity,
      revenueA: itemA.revenue,
      quantityB: itemB.quantity,
      revenueB: itemB.revenue,
    }
  })

  const top10Data = combinedData.sort((a, b) => b[sortBy === 'quantity' ? 'quantityB' : 'revenueB'] - a[sortBy === 'quantity' ? 'quantityB' : 'revenueB']).slice(0, 10)

  // Store this data for the AI
  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  const aiDataKey = sortBy === 'quantity' ? 'top10ByQuantity' : 'top10ByRevenue'
  alsoStore(top10Data, (v) => ({
    [aiDataKey]: deepmerge(...v.map((item, index) => {
      const valueA = sortBy === 'quantity' ? item.quantityA : item.revenueA
      const valueB = sortBy === 'quantity' ? item.quantityB : item.revenueB
      let changePercent = 0
      if (valueA > 0) {
        changePercent = (valueB - valueA) / valueA
      } else if (valueB > 0) {
        changePercent = 1 // Represent 'New' as 100% growth
      }
      
      return {
        [`rank${index + 1}`]: {
          name: item.name,
          category: item.category,
          periodA_Value: sortBy === 'quantity' ? formatNumberUtil(valueA) : formatCurrencyUtil(valueA),
          periodB_Value: sortBy === 'quantity' ? formatNumberUtil(valueB) : formatCurrencyUtil(valueB),
          change: formatDecimalBasedPercentage(changePercent, 1)
        }
      }
    }))
  }))


  if (top10Data.length === 0 || (sortBy === 'quantity' && top10Data.every((item) => item.quantityB === 0 && item.quantityA === 0))) {
    container.innerHTML = '<p class="text-gray-400 text-center py-8">No menu data available for this period.</p>'
    return
  }

  let tableHtml = `<div class="overflow-x-auto"><table class="min-w-full text-sm">
        <thead class="text-left text-gray-500"><tr>
            <th class="p-2 font-medium">Menu Name</th>
            <th class="p-2 font-medium text-right">Period A</th>
            <th class="p-2 font-medium text-right">Period B</th>
            <th class="p-2 font-medium text-right">Change</th>
        </tr></thead><tbody>`

  top10Data.forEach((item) => {
    const valueA = sortBy === 'quantity' ? item.quantityA : item.revenueA
    const valueB = sortBy === 'quantity' ? item.quantityB : item.revenueB
    const displayA = sortBy === 'quantity' ? formatNumber(valueA) : formatCurrency(valueA)
    const displayB = sortBy === 'quantity' ? formatNumber(valueB) : formatCurrency(valueB)

    let changeText = 'N/A'
    let changeColor = 'text-gray-500'
    if (valueA > 0) {
      const change = ((valueB - valueA) / valueA) * 100
      changeText = `${change > 0 ? '+' : ''}${change.toFixed(0)}%`
      if (change > 0) changeColor = 'text-green-600'
      if (change < 0) changeColor = 'text-red-600'
    } else if (valueB > 0) {
      changeText = 'New'
      changeColor = 'text-green-600'
    }

    tableHtml += `<tr class="border-t">
            <td class="p-2 font-medium text-gray-800">${item.name}</td>
            <td class="p-2 text-right font-mono">${displayA}</td>
            <td class="p-2 text-right font-mono">${displayB}</td>
            <td class="p-2 text-right font-mono font-semibold ${changeColor}">${changeText}</td>
        </tr>`
  })

  tableHtml += `</tbody></table></div>`
  container.innerHTML = tableHtml
}
// --- END MODIFICATION (JOB_14) ---